/**
 * CategoryTreePicker.jsx · v3.5.1 · Selector jerárquico product.category
 * Alimentado por GET /api/v1/catalog/categories/tree
 */
import { useEffect, useMemo, useRef, useState } from "react";
import FloatingPanel from "./ui/FloatingPanel";   // v101.98.0 · lista fuera de tablas / modales
import {
  ChevronRight, ChevronDown, FolderTree, Search, Check, Loader2, X,
} from "lucide-react";
import api from "../lib/api";

// Aplana el árbol y anota depth para render iterativo (sin recursión JSX)
function flattenTree(roots, expanded, out, depth) {
  for (let i = 0; i < roots.length; i++) {
    const n = roots[i];
    const hasChildren = n.children && n.children.length > 0;
    out.push({
      id: n.id,
      name: n.name,
      complete_name: n.complete_name,
      depth: depth,
      hasChildren: hasChildren,
      isOpen: expanded.has(n.id),
    });
    if (hasChildren && expanded.has(n.id)) {
      flattenTree(n.children, expanded, out, depth + 1);
    }
  }
  return out;
}

export default function CategoryTreePicker(props) {
  const anchorRef = useRef(null);   // v101.98.0
  const value = props.value;
  const onChange = props.onChange;
  const placeholder = props.placeholder || "Elige categoría…";
  const compact = !!props.compact;

  const [open, setOpen] = useState(false);
  const [tree, setTree] = useState([]);
  const [byId, setById] = useState({});
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    if (tree.length > 0) return;
    setLoading(true);
    api.get("/v1/catalog/categories/tree")
      .then(function (res) {
        const data = res.data;
        const roots = data.roots || [];
        setTree(roots);
        const map = {};
        const stack = roots.slice();
        while (stack.length) {
          const n = stack.pop();
          map[n.id] = { id: n.id, name: n.name, complete_name: n.complete_name };
          if (n.children) {
            for (let i = 0; i < n.children.length; i++) stack.push(n.children[i]);
          }
        }
        setById(map);
        const initExp = new Set();
        for (let i = 0; i < roots.length; i++) initExp.add(roots[i].id);
        setExpanded(initExp);
      })
      .catch(function () { /* silent */ })
      .finally(function () { setLoading(false); });
  }, [open, tree.length]);

  const selected = value != null ? byId[value] : null;
  const selectedParentPath = selected && selected.complete_name
    ? selected.complete_name.split(" / ").slice(0, -1).join(" / ")
    : "";

  const flatMatches = useMemo(function () {
    if (!q.trim()) return null;
    const t = q.trim().toLowerCase();
    const arr = Object.values(byId).filter(function (c) {
      return (c.complete_name || "").toLowerCase().indexOf(t) >= 0;
    });
    return arr.slice(0, 60);
  }, [q, byId]);

  const flatList = useMemo(function () {
    if (flatMatches != null) return null;
    return flattenTree(tree, expanded, [], 0);
  }, [tree, expanded, flatMatches]);

  function toggleNode(id) {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  }

  function pickNode(node) {
    if (onChange) onChange(byId[node.id] || node);
    setOpen(false);
    setQ("");
  }

  return (
    <div className="relative" data-testid="cat-tree-picker">
      <button
        ref={anchorRef}
        type="button"
        onClick={function () { setOpen(!open); }}
        className={
          "w-full flex items-center gap-2 border border-gray-300 rounded bg-white hover:border-[#0A3747] text-left " +
          (compact ? "px-2 py-1" : "px-3 py-2")
        }
        data-testid="cat-tree-trigger"
      >
        <FolderTree size={compact ? 12 : 14} className="text-[#0A3747] flex-shrink-0" />
        <span className="flex-1 min-w-0 text-xs truncate">
          {selected ? (
            <span>
              {selectedParentPath ? (
                <span className="text-gray-500">{selectedParentPath} / </span>
              ) : null}
              <span className="font-bold text-[#0A3747]">{selected.name}</span>
            </span>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </span>
        {selected ? (
          <span
            onClick={function (e) { e.stopPropagation(); if (onChange) onChange(null); }}
            className="text-gray-400 hover:text-red-500 cursor-pointer"
            data-testid="cat-tree-clear"
          >
            <X size={12} />
          </span>
        ) : null}
        <ChevronDown size={12} className="text-gray-400 flex-shrink-0" />
      </button>

      {open ? (
        <>
          <div className="fixed inset-0 z-[100]" onClick={function () { setOpen(false); }} />
          <FloatingPanel
            anchorRef={anchorRef}
            matchWidth
            minWidth={260}
            className="bg-white border border-gray-200 rounded-lg shadow-xl flex flex-col"
            data-testid="cat-tree-dropdown"
          >
            <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <Search size={12} className="text-gray-400" />
              <input
                autoFocus
                value={q}
                onChange={function (e) { setQ(e.target.value); }}
                placeholder="Buscar en el árbol…"
                className="flex-1 text-xs outline-none bg-transparent"
                data-testid="cat-tree-search"
              />
            </div>

            <div className="overflow-y-auto flex-1 py-1">
              {loading ? (
                <div className="flex items-center gap-1.5 px-3 py-4 text-xs text-gray-500">
                  <Loader2 size={12} className="animate-spin" /> Cargando árbol…
                </div>
              ) : null}

              {!loading && flatMatches != null ? (
                <ul>
                  {flatMatches.length === 0 ? (
                    <li className="px-3 py-3 text-xs text-gray-400 italic">Sin coincidencias</li>
                  ) : null}
                  {flatMatches.map(function (c) {
                    const parentPath = (c.complete_name || "").split(" / ").slice(0, -1).join(" / ");
                    return (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={function () { pickNode(c); }}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-[#F3F4F6] flex items-center gap-1.5"
                          data-testid={"cat-tree-flat-" + c.id}
                        >
                          <FolderTree size={10} className="text-gray-400 flex-shrink-0" />
                          <span className="flex-1">
                            {parentPath ? (
                              <span className="text-gray-500">{parentPath} / </span>
                            ) : null}
                            <span className="font-bold text-[#0A3747]">{c.name}</span>
                          </span>
                          {value === c.id ? <Check size={11} className="text-emerald-600" /> : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}

              {!loading && flatMatches == null && flatList ? (
                <ul>
                  {flatList.map(function (row) {
                    return (
                      <li key={row.id}>
                        <div
                          className={
                            "flex items-center gap-1 text-xs pr-2 hover:bg-[#F3F4F6] " +
                            (value === row.id ? "bg-[#FEF3C7]" : "")
                          }
                          style={{ paddingLeft: 8 + row.depth * 12 }}
                          data-testid={"cat-tree-node-" + row.id}
                        >
                          <button
                            type="button"
                            onClick={function () { if (row.hasChildren) toggleNode(row.id); }}
                            className="w-4 flex-shrink-0 text-gray-400 hover:text-gray-700"
                            aria-label="Expandir"
                            data-testid={"cat-tree-toggle-" + row.id}
                          >
                            {row.hasChildren ? (
                              row.isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />
                            ) : (
                              <span className="inline-block w-3" />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={function () { pickNode(row); }}
                            className="flex-1 text-left py-1 flex items-center gap-1.5 min-w-0"
                            data-testid={"cat-tree-pick-" + row.id}
                          >
                            <FolderTree size={10} className="text-gray-400 flex-shrink-0" />
                            <span className={"truncate " + (value === row.id ? "font-bold text-[#0A3747]" : "")}>
                              {row.name}
                            </span>
                            {value === row.id ? (
                              <Check size={11} className="text-emerald-600 flex-shrink-0" />
                            ) : null}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          </FloatingPanel>
        </>
      ) : null}
    </div>
  );
}
